import React from 'react';
import { useIntl } from 'react-intl';
import {
	Box,
	Paper,
	styled,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	type TableCellProps
} from '@mui/material';

import { Icon, useSettings, Utils } from 'app/main/components';

export interface CompaniesFinancialStatementField {
	className?: string;
	name: string;
	style?: 'currency' | 'percent';
	trend?: 'positive' | 'negative';
}

interface CompaniesFinancialStatementsTableProps {
	title: string;
	fields: CompaniesFinancialStatementField[];
	financialStatements: any[];
	dateField: string;
}

const FieldTableCell = styled(({ children, ...props }: TableCellProps) => {
	return (
		<TableCell {...props}>
			<div>{children}</div>
		</TableCell>
	);
})(({ theme }) => ({
	width: '200px',
	minWidth: '200px',
	[theme.breakpoints.up('sm')]: {
		width: '256px',
		minWidth: '256px'
	},
	position: 'sticky',
	left: 0,
	padding: 0,
	backgroundColor: theme.palette.background.paper,
	'& > div': {
		minHeight: '37px',
		padding: '6px 16px',
		borderRight: `1px solid ${theme.palette.divider}`,
		alignContent: 'center'
	}
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	'&:last-child > .MuiTableCell-root': {
		borderBottom: 0
	}
}));

function parseValue(value, style) {
	if (!value && value !== 0) {
		return '-';
	}
	if (style === 'currency') {
		return Utils.formatCurrency(value, { maximumFractionDigits: 0 });
	}
	if (style === 'percent') {
		return Utils.formatPercentage(value, { minimumFractionDigits: 2, signDisplay: 'exceptZero' });
	}

	return value;
}

function TableValueCell({ field, financialStatement, previousStatement }) {
	const value = financialStatement[field.name];
	const previousValue = previousStatement?.[field.name];

	const result = field.trend === 'positive' ? value >= previousValue : value < previousValue;
	const resultValue = previousStatement
		? ((value - previousValue) * 100) / Math.abs(previousValue || value)
		: undefined;

	return (
		<>
			<TableCell className="tabular-nums" align="right">
				<Typography>{parseValue(value, field.style)}</Typography>
			</TableCell>
			{previousStatement ? (
				<TableCell className="tabular-nums">
					{field.trend && !!previousValue && !!previousValue && !!resultValue && (
						<Box className="flex items-center" color={result ? 'success.main' : 'error.main'}>
							<Icon className="mr-4" icon={value >= previousValue ? 'trending-up' : 'trending-down'} />
							<Typography variant="caption">{parseValue(resultValue, 'percent')}</Typography>
						</Box>
					)}
				</TableCell>
			) : (
				<></>
			)}
		</>
	);
}

export default function CompaniesFinancialStatementsTable(props: CompaniesFinancialStatementsTableProps) {
	const { t } = useSettings();
	const intl = useIntl();

	return (
		<TableContainer className="rounded-8" component={Paper} variant="outlined">
			<Table size="small">
				<TableHead>
					<TableRow>
						<FieldTableCell>{t(props.title)}</FieldTableCell>
						{props.financialStatements?.map((financialStatement, index) => (
							<React.Fragment key={index}>
								<TableCell className="w-128 tabular-nums" align="right">
									{intl.formatDate(financialStatement[props.dateField], {
										year: 'numeric',
										month: '2-digit',
										day: '2-digit'
									})}
								</TableCell>
								{index !== props.financialStatements?.length - 1 && <TableCell className="w-128" />}
							</React.Fragment>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{props.fields?.map((field, i) => (
						<StyledTableRow key={i}>
							<FieldTableCell className={field.className}>{t(field.name)}</FieldTableCell>
							{props.financialStatements?.map((financialStatement, index) => (
								<TableValueCell
									key={index}
									field={field}
									financialStatement={financialStatement}
									previousStatement={
										index < props.financialStatements.length
											? props.financialStatements[index + 1]
											: undefined
									}
								/>
							))}
						</StyledTableRow>
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
}
